import { useTheme } from '../hooks/useTheme';

/** Supported logo color variants. */
export type AccedoLogoVariant = 'light' | 'dark' | 'auto';

interface AccedoLogoProps {
    /** Width and height of the logo in pixels. Defaults to 32. */
    size?: number;
    /**
     * Color variant to use.
     * - `'light'` — indigo legs, teal checkmark (for light backgrounds)
     * - `'dark'` — violet legs, teal checkmark (for dark backgrounds)
     * - `'auto'` — follows the current theme (default)
     */
    variant?: AccedoLogoVariant;
    /** Optional accessible label. Defaults to "Accedo logo". */
    ariaLabel?: string;
}

/**
 * AccedoLogo renders the Accedo brand mark inline as an SVG.
 *
 * The logo is an open "A" shape with a teal checkmark as the crossbar.
 * In light mode, the legs are indigo (#4338CA). In dark mode, the legs are
 * violet (#6366F1). The checkmark is always teal (#0D9488 / #14B8A6).
 */
export function AccedoLogo({ size = 32, variant = 'auto', ariaLabel = 'Accedo logo' }: AccedoLogoProps) {
    const { isDark } = useTheme();

    const useDark = variant === 'dark' || (variant === 'auto' && isDark);

    const legColor = useDark ? '#6366F1' : '#4338CA';
    const checkColor = useDark ? '#14B8A6' : '#0D9488';

    return (
        <svg
            viewBox="0 0 200 200"
            xmlns="http://www.w3.org/2000/svg"
            width={size}
            height={size}
            aria-label={ariaLabel}
            role="img"
            style={{ display: 'inline-block', flexShrink: 0 }}
        >
            {/* Left leg of the A */}
            <path
                d="M100 25 C97 25, 94 28, 92 32 L38 165 C36 170, 38 175, 44 175 L62 175 C66 175, 69 172, 71 168 L100 88 Z"
                fill={legColor}
            />
            {/* Right leg of the A */}
            <path
                d="M100 25 C103 25, 106 28, 108 32 L162 165 C164 170, 162 175, 156 175 L138 175 C134 175, 131 172, 129 168 L100 88 Z"
                fill={legColor}
            />
            {/* Teal checkmark crossbar */}
            <polyline
                points="58,118 82,140 142,102"
                fill="none"
                stroke={checkColor}
                strokeWidth="16"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

export default AccedoLogo;
